import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import { PagerTargetEvent } from '@progress/kendo-react-data-tools';
import {
  GridCellProps,
  GridCustomCellProps,
  GridFooterCellProps,
  GridPageChangeEvent,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid';
import { produce } from 'immer';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../../../../../../../common/global/helpers';
import Grid, { GridCellTd } from '../../../../../../../common/widgets/Grid';
import Loader from '../../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../../common/widgets/LoadingSpinner';
import { config } from '../../../../../config';
import { BudgetTransactionModel } from '../../../../../models/BudgetTransactionModel';
import { resources } from '../../../../../resources/resources';
import { employeeAdminService } from '../../../../../services/employee-admin';

interface TransactionsProps {
  employeeId: number;
  needReload: boolean;
  reloadDone: () => void;
}

interface TransactionsState {
  transactions: BudgetTransactionModel[];
  showErrorPanel: boolean;
}

interface PageState {
  skip: number;
  take: number;
}

const DateCell = (props: GridCustomCellProps) => {
  const { dataItem } = props;
  return (
    <GridCellTd>{moment(dataItem.dateAdded).format('YYYY-MM-DD')}</GridCellTd>
  );
};

const DescriptionCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>{dataItem.description}</GridCellTd>
);

const AmountCell = (props: GridCustomCellProps) => {
  const { dataItem } = props;
  return (
    <GridCellTd className="grid-number">
      {`${formatPrice(dataItem.amount)} ${config.currency}`}
    </GridCellTd>
  );
};

interface DescriptionFooterCellProps extends GridFooterCellProps {
  label: string;
}

const DescriptionFooterCell = (props: DescriptionFooterCellProps) => {
  const { label } = props;
  return (
    <GridCellTd>
      {label}
      {':'}
    </GridCellTd>
  );
};

interface AmountFooterCellProps extends GridFooterCellProps {
  total: number;
}

const AmountFooterCell = (props: AmountFooterCellProps) => {
  const { total } = props;

  return (
    <GridCellTd className="grid-number">
      {`${formatPrice(total)} ${config.currency}`}
    </GridCellTd>
  );
};

export default function Transactions(props: Readonly<TransactionsProps>) {
  const { t: translate } = useTranslation();
  const { employeeId, needReload, reloadDone } = props;
  const initialSort = useMemo(
    () =>
      [
        {
          field: 'dateAdded',
          dir: 'desc',
        },
      ] as SortDescriptor[],
    [],
  );

  const initialPageState: PageState = {
    skip: 0,
    take: config.grid.defaultPageSize,
  };

  const [state, setState] = useState<TransactionsState>(
    {} as TransactionsState,
  );
  const [page, setPage] = useState<PageState>(initialPageState);
  const [sort, setSort] = useState<SortDescriptor[]>(initialSort);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >(config.grid.defaultPageSize);

  const loadTransactions = useCallback(
    async (abortController?: AbortController) => {
      const response = await employeeAdminService.getBudgetTransactions(
        employeeId,
        {
          signal: abortController?.signal,
        },
      );
      if (!response.hasErrors) {
        setState(
          produce(d => {
            d.transactions = orderBy(response.data, initialSort);
          }),
        );
        reloadDone();
      } else {
        setState(
          produce(d => {
            d.showErrorPanel = true;
          }),
        );
      }
    },
    [employeeId, reloadDone, initialSort],
  );

  const handleSortChange = (e: GridSortChangeEvent) => {
    setState(
      produce(draft => {
        draft.transactions = orderBy(state.transactions ?? [], e.sort);
      }),
    );
    setSort(e.sort);
  };

  const handlePageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === 'All'
        ? state.transactions?.length ?? 0
        : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  return (
    <div className="admin-grid employee-transactions-grid">
      <Loader
        loading={!state.transactions || needReload}
        loader={<LoadingSpinner />}
        showErrorPanel={state.showErrorPanel}
        errorPanelMessage={''}
      >
        <Grid
          data={orderBy(
            state.transactions?.length
              ? state.transactions.slice(page.skip, page.take + page.skip)
              : [],
            sort,
          )}
          pageable={{
            pageSizes: config.grid.pageSizes,
            pageSizeValue,
            info: false,
            previousNext: true,
          }}
          skip={page.skip}
          take={page.take}
          total={state.transactions?.length ?? 0}
          sortable={{ allowUnsort: true, mode: 'multiple' }}
          sort={sort}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          columns={[
            {
              title: translate(
                resources.admin.employees.details.trasactions.grid.dateAdded,
              ),
              field: 'dateAdded',
              cell: DateCell,
            },
            {
              title: translate(
                resources.admin.employees.details.trasactions.grid.description,
              ),
              field: 'description',
              cell: DescriptionCell,
              footerCell: props => (
                <DescriptionFooterCell
                  {...props}
                  label={translate(
                    resources.admin.employees.details.trasactions.grid.balance,
                  )}
                />
              ),
            },
            {
              title: translate(
                resources.admin.employees.details.trasactions.grid.amount,
              ),
              field: 'amount',
              cell: AmountCell,
              headerClassName: 'grid-number',
              footerCell: props => (
                <AmountFooterCell
                  {...props}
                  total={state.transactions.reduce(
                    (acc, current) => acc + current.amount,
                    0,
                  )}
                />
              ),
            },
          ]}
        />
      </Loader>
    </div>
  );
}
